import * as THREE from 'three';
import * as TWEEN from 'tween.js';
import * as OrbitControls from 'three-orbitcontrols';
import * as FBXLoader from 'three-fbxloader-offical';
import normalNoise from '../assets/models/bump-maps/normal-noise-02.png';
import normalNoise3 from '../assets/models/bump-maps/normal-noise-03.jpg';

const locatorIds = [
    'c_rod_body',
    'c_rod_pili',
    'c_rod_flagellum',
    'c_vibrio_flagellum',
    'c_vibrio_body',
    'c_coccus_capsule',
    'c_coccus_capsule1',
    'c_spiro_flagellum'
];


export default ( canvas, bacteria ) => {
    const clock = new THREE.Clock();
    const origin = new THREE.Vector3(0,0,0);
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    const loader = new FBXLoader();

    const screenDimensions = {
        width: canvas.width,
        height: canvas.height
    };

    const bumpMap2 = new THREE.TextureLoader().load( normalNoise );
    const bumpMap3 = new THREE.TextureLoader().load( normalNoise3 );

    bumpMap2.wrapS = THREE.RepeatWrapping;
    bumpMap2.wrapT = THREE.RepeatWrapping;

    bumpMap3.wrapS = THREE.RepeatWrapping;
    bumpMap3.wrapT = THREE.RepeatWrapping;
    bumpMap3.repeat.set( 6, 6 );

    const scene = buildScene();
    const renderer = buildRender(screenDimensions);
    const camera = buildCamera(screenDimensions);
    const controls = buildControls();

    addLights();
    const mixers =  loadAndBuildMixers();

    update();

    function buildScene() {
        const scene = new THREE.Scene();
        return scene;
    }

    function buildRender({ width, height}) {
        const gl = canvas.getContext("webgl");
        let renderer = null;
        if(true || (gl && gl instanceof WebGLRenderingContext) ){
            const DPR = window.devicePixelRatio ? window.devicePixelRatio : 1;
            renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true});
            renderer.setPixelRatio(DPR);
            renderer.setSize(width, height);

            renderer.gammaInput = true;
            renderer.gammaOutput = true;
            renderer.shadowMap.enabled = true;
        }else{
            // TODO: Support/Alternative for IE Users.
        }

        return renderer;
    }

    function buildCamera({ width, height}) {
        const aspectRatio = width / height;
        const fieldOfView = 60;
        const nearPlane = 0.1;
        const farPlane = 2000;

        const camera = new THREE.PerspectiveCamera(fieldOfView,aspectRatio,nearPlane,farPlane);

        if( bacteria.cameraPos !== undefined ){
            camera.position.set( bacteria.cameraPos.x, bacteria.cameraPos.y, bacteria.cameraPos.z );
        }else {
            camera.position.set(0, 10, 15);
        }

        camera.lookAt(origin);

        return camera;
    }

    function buildControls(){
        const controls = new OrbitControls(camera, renderer.domElement);
        controls.target.set(0,0,0);
        controls.update();
        controls.enablePan = false;
        controls.enableZoom = false;
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.rotateSpeed = 0.02;

        return controls;
    }

    function addLights() {
        const hempisherelight = new THREE.HemisphereLight( 0xffffff, 0x383838 );
        hempisherelight.position.set( 0, 200, 0 );

        const spotLight = new THREE.PointLight();
        spotLight.position.set( 500, 200, 0 );
        spotLight.intensity = .5;
        spotLight.lookAt( 0 ,0 ,0 );

        const spotLight2 = new THREE.PointLight();
        spotLight2.position.set( -1000, 200, 600 );
        spotLight2.intensity = .6;
        spotLight2.lookAt( 0, 0, 0 );

        scene.add(hempisherelight);
        scene.add(spotLight);
        scene.add(spotLight2);

    }

    function loadAndBuildMixers() {
        let mixers = [];

        loader.load(bacteria.model, (object) => {

            object.mixer = new THREE.AnimationMixer( object );
			mixers.push( object.mixer );

			if( object.animations !== undefined && object.animations.length > 0 ) {
                let action = object.mixer.clipAction(object.animations[0]);
                action.play();
            }

			object.traverse( function ( child ) {
				if ( child.isMesh ) {
					child.castShadow = true;
					child.recieveShadow = true;
				}

				if( child.name === "m_rod3" ){
				    child.material[1].bumpMap = bumpMap2;
                }

                if( child.name === "m_branched" ){
				    child.material.bumpMap = bumpMap2;
                }

                if( child.name === "Cocus7_skin" || child.name === "Cocus6_skin" || child.name === "Cocus5_skin" ||
                    child.name === "Cocus4_skin" || child.name === "Cocus3_skin" || child.name === "Cocus2_skin" ||
                    child.name === "Cocus1_skin" || child.name === "Cocus0_skin" ){
				    child.material.bumpMap = bumpMap3;
				    child.material.bumpScale = 0.05;
				    child.material.opacity = 0.5;
                }

                if( child.name === "m_vibrio1" ){
				    child.material.bumpMap = bumpMap3;
				    child.material.bumpScale = 0.1;
                }

                if( locatorIds.indexOf( child.name ) > -1 ) {
				    child.material.opacity = 0;
				    child.material.transparent = true;
                }

                if( child.name === "spiro_skin" ){
				    child.material.opacity = 0.7;
                }
			});

			scene.add(object);
        });

        return mixers;
    }

    function update() {

        const elapsedTime = clock.getDelta();

        for(let i=0; i<mixers.length;i++){
            mixers[i].update(elapsedTime);
        }

        TWEEN.update();
        controls.update();
        renderer.render(scene, camera);
    }

    function onWindowResize() {
        const { width, height } = canvas;

        screenDimensions.width = width;
        screenDimensions.height = height;

        camera.aspect = width / height;
        camera.updateProjectionMatrix();

        renderer.setSize(width, height);
    }

    function onClick(posX,posY){
        mouse.x = posX;
        mouse.y = posY;

        raycaster.setFromCamera( mouse, camera );
        const clicked = raycaster.intersectObjects( scene.children, true );

        if(clicked.length > 0){
            const closest = clicked[0];
            const closestLocator = bacteria.features.filter( (feature, key) => { feature.key = key; return feature.locatorId === closest.object.name} );
            if(closestLocator.length > 0){
                const { cameraPos } = closestLocator[0];
                animateCameraMovement( camera.position, cameraPos );
                return closestLocator[0].key;
            }
        }
    }

    function animateCameraMovement( from ,to ){
        new TWEEN.Tween(from)
            .to( to, 1000 )
            .easing(TWEEN.Easing.Quartic.InOut)
            .onUpdate( function(){
               camera.position.set(this.x,this.y,this.z);
               camera.lookAt(origin);
            })
            .start();
    }

    function moveCameraTo(feature){
        animateCameraMovement( camera.position, feature.cameraPos );
    }

    function getObjectPosition(feature){
        const locator = scene.getObjectByName(feature.locatorId, true);
        const point = new THREE.Vector3();
        locator.geometry.computeBoundingBox();
        const boundingBox = locator.geometry.boundingBox;

        // https://stackoverflow.com/questions/14211627/three-js-how-to-get-position-of-a-mesh
        point.subVectors( boundingBox.max, boundingBox.min );
        point.multiplyScalar(0.5);
        point.add( boundingBox.min );
        point.applyMatrix4( locator.matrixWorld );

        point.project( camera );

        return {
            x: ( point.x + 1 ) * canvas.width / 2,
            y: - ( point.y - 1 ) * canvas.height / 2
        };
    }

    return {
        update,
        onWindowResize,
        onClick,
        moveCameraTo,
        getObjectPosition
    }
}



// WEBPACK FOOTER //
// ./src/threejs/scene-manager.js